<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="showPaperTransferDialog"
            :action-loading="transferDialogLoading"
            @on-confirm="handleTransferYes"
            @on-abort="handleTransferNo"
        >
            <template #title>
                Przeniesienie cech papieru
            </template>
            <template #content>
                <p class="text-body1">
                    Wykryto ten sam papier jak w poprzednim zleceniu. Czy chcesz przenieść cechy papieru?
                </p>
            </template>
            <template #confirmButton>
                Tak
            </template>
            <template #abortButton>
                Nie
            </template>
        </ConfirmDialog>

        <page-title>Raportowanie papieru / Raporty szczegółowe</page-title>

        <v-container>
            <v-row align="center">
                <v-col class="pb-0">
                    <v-btn text color="primary" class="pa-0" @click="close">
                        <v-icon color="primary">
                            mdi-arrow-left
                        </v-icon>
                        Powrót do listy
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <ReportTimerTracker
                        :report="report"
                        :task-name="taskName"
                        :delay="delay"
                        :duration="duration"
                        :time-without-limit="timeWithoutLimit"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <ReportParametersData :report-data="report" />
                </v-col>
                <v-col cols="12" md="6">
                    <PaperPalletReport :pallets-list="report.reportPallets" :report-id="report.id" />
                </v-col>
            </v-row>
            <v-row v-if="productionSiteData">
                <NextScheduledTasks
                    :limit="isOffsetPrintSite ? 3 : 1"
                    :is-printing="isOffsetPrintSite"
                    :machine-code="report.machineCode"
                />
            </v-row>
        </v-container>
    </v-container>
</template>

<script>
import { errorHandler } from '@/utils/errorHandler'
import NextScheduledTasks from '../Common/NextScheduledTasks'
import PageTitle from '@/components/common/PageTitle'
import PaperPalletReport from './PaperPalletReport'
import ReportParametersData from '../Common/ReportParametersData'
import ReportTimerTracker from '../Common/ReportTimerTracker'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import store from '@/store'

export default {
    name: 'DetailedPaperReports',
    components: {
        PageTitle,
        PaperPalletReport,
        NextScheduledTasks,
        ReportParametersData,
        ReportTimerTracker,
        ConfirmDialog
    },
    beforeRouteEnter(to, from, next) {
        if (to.params.id) {
            Promise.all([
                store.dispatch('reports/getPaperReportingReport', to.params.id),
                store.dispatch('reports/getTaskNames')
            ])
                .then(([reportData, taskNames]) => {
                    return store.dispatch('configurations/getProductionSite', reportData.productionSiteId)
                        .then(productionSiteData => ({ reportData, taskNames, productionSiteData }))
                })
                .then(({ reportData, taskNames, productionSiteData }) => {
                    next(vm => {
                        vm.isLoading = true
                        vm.taskNames = taskNames.data.results
                        vm.taskName = vm.taskNames.find(item => item.id === reportData.objectType).name
                        vm.report = reportData
                        vm.productionSiteData = productionSiteData.data
                    })
                })
                .catch(error => {
                    errorHandler(error)
                    next(false)
                })
        } else {
            next()
        }
    },
    data() {
        return {
            productionSiteData: null,
            taskName: null,
            taskNames: [],
            delay: 0,
            duration: null,
            intervalId: null,
            remainingTime: null,
            timeWithoutLimit: 0,
            showPaperTransferDialog: false,
            transferDialogLoading: false,
            lastPaperParameter: null,
            report: {
                id: null,
                statusId: null,
                objectDetail: {
                    reportItemPlateParameters: []
                },
                reportPallets: [],
                comments: [],
                productionSite: {
                    machine: {
                        code: null
                    }
                },
            },
        }
    },
    computed: {
        isReportFinished() {
            return this.report?.statusId == 99
        },
        isOffsetPrintSite() {
            return this.productionSiteData && this.productionSiteData.typeId === 7
        },
    },
    mounted() {
        if (!this.isReportFinished) this.setDuration()
        this.checkForPaperTransferOpportunity()
    },
    methods: {
        checkForPaperTransferOpportunity() {
            if (!this.report.lastUsedParameter) {
                return
            }

            this.lastPaperParameter = this.report.lastUsedParameter
            this.showPaperTransferDialog = true
        },
        handleTransferYes() {
            if (this.report.reportPallets.find(pallet => pallet.parameter === this.lastPaperParameter)) {
                this.$store.dispatch('showNotification', {
                    type: 'primary',
                    message: 'Wprowadzony numer został już wcześniej dodany.'
                })
                this.showPaperTransferDialog = false

                return
            }

            this.transferDialogLoading = true

            this.$store.dispatch('reports/checkPalletParameter', this.lastPaperParameter)
                .then(() => {
                    return this.$store.dispatch('reports/addPallet', {
                        reportId: this.report.id,
                        parameter: this.lastPaperParameter
                    })
                })
                .then(response => {
                    this.report.reportPallets.push(response)
                    this.showPaperTransferDialog = false
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.transferDialogLoading = false
                })
        },
        handleTransferNo() {
            this.showPaperTransferDialog = false
        },
        setDuration() {
            const timeElapsed = Math.floor((this.report.remainingTime) / 1000)

            this.report.remainingTime == null ? this.remainingTime = 0 : this.remainingTime = this.report.remainingTime

            if (timeElapsed >= this.remainingTime) { // przekroczono czas wykonania = jest opoznienie lub nie ma limitu
                this.report.remainingTime == null ? this.timeWithoutLimit = timeElapsed : this.delay = Math.floor(timeElapsed - this.remainingTime)
                this.duration = -1
            }
            if (timeElapsed < this.remainingTime) { // miescimy sie w czasie
                this.duration = Math.floor(this.remainingTime - timeElapsed)
            }
            if (this.report.statusId === 1) {
                this.refreshDuration()
                this.intervalId = setInterval(this.refreshDuration, 1000)
            }
        },
        refreshDuration() {
            if (this.duration >= 0) {
                this.duration -= 1
            }
            if (this.duration == -1) {
                this.report.remainingTime == null ? this.timeWithoutLimit += 1 : this.delay += 1
            }
        },
        close() {
            this.$router.push({ name: 'reportsPaperReportingPaperReporting' })
                .catch(error => {
                    errorHandler(error)
                })
        },
    },
}
</script>